import { TwoFaEntity } from 'models/account';
import { Device, DeviceCodeName, DeviceTypeName } from 'models/device';
import { objectEnum } from 'utils/helpers';
import { BaseModel, ContextType, Elements, Ref, Uuid } from './common';
import { Reseller } from './reseller';
import { AppCollectionStyles, AppCollectionGroup, AppMarketPermissions } from './enums';

export type MerchantPlanName =
  | 'Register'
  | 'Classic Bundle'
  | 'Payments Bundle'
  | 'Payments Plus Bundle'
  | 'Register Bundle'
  | 'Register Lite Bundle'
  | 'Dining ';
export type MerchantPlanCode = 'CO_CLASSIC' | 'CO_TERMINAL' | 'CO_REGISTER' | 'CO_REGISTER_LITE';

export enum MerchantPlanType {
  CLASSIC = 'CLASSIC',
  PAYMENTS_PLUS = 'PAYMENTS_PLUS',
  PAYMENTS = 'PAYMENTS',
  REGISTER = 'REGISTER',
  REGISTER_LITE = 'REGISTER_LITE',
  DINING = 'DINING',
  QSR = 'QSR',
  NO_HARDWARE = 'NO_HARDWARE'
}

export type MerchantPlanModuleName =
  | 'items'
  | 'item_modifiers'
  | 'item_variants'
  | 'item_exchanges'
  | 'weigh_per_unit_items'
  | 'orders'
  | 'combine_assign_orders'
  | 'order_printers'
  | 'order_types'
  | 'table_management'
  | 'restaurant_shift_management'
  | 'bar_tab_management'
  | 'service_charges'
  | 'automatic_discounts'
  | 'customer_preferences_promotions'
  | 'register_preauth'
  | 'employees'
  | 'discounts'
  | 'merchant'
  | 'tax_rates'
  | 'cash'
  | 'customers'
  | 'payments'
  | 'printers'
  | 'fast_pay'
  | 'cost_of_goods'
  | 'table_service'
  | 'advanced_reporting'
  | 'purchasing_card';

export interface MerchantPlanModule {
  id: Uuid;
  name: MerchantPlanModuleName;
}

export type MerchantPlanMeta = {
  merchantPlans: Elements<MerchantPlan>;
  peripherals: Elements<Partial<PeripheralBundleCountry>>;
  currentMerchantPlan: MerchantPlan;
  rules: MerchantPlanRules;
}

export enum EnforcePlanAuthorization {
  ALWAYS = 'ALWAYS',
  NEVER = 'NEVER',
  INITIAL = 'INITIAL',
}

export enum EnforceAdditionalAppsAuthorization {
  ALWAYS = 'ALWAYS',
  INITIAL = 'INITIAL',
}

export type MerchantPlanRules = {
  /** Flag for OOBE whether or not to enforce plan authorization and how often. */
  enforcePlanAuthorization: EnforcePlanAuthorization;
  /** Flag for OOBE whether or not to show apps authorization. */
  enforceAdditionalAppsAuthorization: EnforceAdditionalAppsAuthorization;
  /** Flag for OOBE whether or not to show special language about a force plan change based on device restrictions. */
  displayPlanRestrictionAlert: boolean;
  /** Flag to control if notifications (like emails) are sent when a force plan change happens based on device restrictions. */
  notifyPlanRestrictionAlert: boolean;
  /** Flag for OOBE and Service Plans page whether or not to show detailed plan pricing information for the plan selection screen */
  displayDetailedPlanSelectionDescription: boolean;
  /** keys/values specific to this endpoint/type */
  highlightModel: { [key: string]: string };
  tosBillingModel: string;
  /** Deprecated but must keep for few app OOBE cycles. */
  displayRecommendedPlanOnServicePlansPage: boolean;
  /** Deprecated but must keep for few app OOBE cycles. */
  enforceChoosePlan: boolean;
}

export type PeripheralBundleCountry = {
  /** Unique identifier */
  readonly id: string;
  price: number;
  /** User-friendly description of pricing for peripheral */
  pricingDescription: string;
  /** The country of the peripheral bundle country */
  country: string;
  deviceTypeName: string;
  equipmentCode: string;
  /** Date/time this bundle was created. */
  createdTime: number;
  /** Date/time this bundle was last modified. */
  modifiedTime: number;
}

// This is different that ContextType. It is given to us from backend for each business entity.
// These entities can be either an enterprise or a merchant due to the hierarchical structure of enterprises.
export type BusinessType = 'MERCHANT' | 'ENTERPRISE';

export type RoleType = 'ADMIN' | 'EMPLOYEE' | 'MANAGER' | 'OWNER';

export type TaskType =
  | 'EMAIL'
  | 'BUSINESS'
  | 'EMPLOYEE'
  | 'INVENTORY_FALLBACK'
  | 'INVENTORY_RESTAURANT'
  | 'TAX'
  | 'CLOVER_DINING'
  | 'YELP'
  | 'GUSTO'
  | 'RAPID_DEPOSIT';

export const ChargeStatusList = [
  'PENDING',
  'IN_PROGRESS',
  'BILLED',
  'CANCELED',
  'REFUND_PENDING',
  'REFUND_PENDING_IF_BILLED',
  'REFUND_IN_PROGRESS',
  'REFUNDED',
  'REFUND_DOWNGRADE_PENDING',
  'REFUND_DOWNGRADE_IN_PROGRESS',
  'REFUND_DOWNGRADE_CANCELED',
  'REFUNDED_DOWNGRADE',
  'DISBURSED',
  'ACH_REJECT',
  'RESUBMITTED',
  'INCURRED',
  'REFUND_INCURRED',
  'REFUND_INCURRED_IF_BILLED',
  'REFUND_DOWNGRADE_INCURRED',
  'REFUND_ACH_REJECT',
  'REFUND_DOWNGRADE_ACH_REJECT',
  'REFUND_CANCELED',
  'WAIVED',
  'REFUND_WAIVED',
  'REFUND_DOWNGRADE_WAIVED',
  'WRITTEN_OFF',
  'REFUND_WRITTEN_OFF',
  'REFUND_DOWNGRADE_WRITTEN_OFF',
  'REVERSED',
  'REFUND_REVERSED',
  'REFUND_DOWNGRADE_REVERSED',
];
export type ChargeStatus = typeof ChargeStatusList[number];

export const ChargeTypeList = ['SUBSCRIPTION', 'METERED', 'PRORATED_SUBSCRIPTION', 'PARTIAL_MONTH'];

export type ChargeType = typeof ChargeTypeList[number];

export type TaskStatus = 'INITIATED' | 'IN_PROGRESS' | 'SKIPPED' | 'COMPLETED' | 'FAILED';

export type Context = Ref & { readonly type: ContextType };

export type DeveloperPermissionName =
  | 'ACCEPT_DEVELOPER_AGREEMENT'
  | 'ACCESS_BANK_DETAILS'
  | 'MANAGE_DEVELOPER_ACCOUNTS'
  | 'MANAGE_MERCHANTS'
  | 'EDIT_APP_PRICING'
  | 'ACCESS_CHARGES'
  | 'MERCHANT_SUPPORT'
  | 'MANAGE_APPS'
  | 'MANAGE_APKS'
  | 'VIEW_MERCHANT_GROUPS'
  | 'EDIT_MERCHANT_GROUPS';

export type ComplianceType = 'HIPAA';

export type CompliancesSourceType = 'ONBOARDING' | 'MANUAL' | 'BACKFILL' | 'MIGRATION';

export interface DeveloperPermission extends BaseModel {
  name: DeveloperPermissionName;
}

export type DeveloperPreferences = {
  pinnedApps: { elements?: Array<{ id: Uuid }> } | Array<{ id: Uuid }>;
};

export interface BaseReport extends Ref {
  startTimestamp: number;
  endTimestamp: number;
}

export type MerchantAddress = Readonly<{
  address1: string;
  address2?: string;
  address3?: string;
  city: string;
  state?: string;
  zip?: string;
  country: CountryCode;
  phoneNumber?: string;
}>;

export type MerchantLogo = Readonly<{
  logoFilename?: string;
  logoType?: string;
  url?: string;
}>;

export interface Compliance
  extends Ref,
    Readonly<{
      createdTime: number;
      enabled: boolean;
      source: CompliancesSourceType;
      type: ComplianceType;
    }> {}

export type Compliances = Elements<Compliance>;

export interface MerchantPlanGroup {
  id: Uuid;
  name: string;
  enforceAssignment: boolean;
  linkable: boolean;
  createdTime: number;
  modifiedTime: number;
}

export interface MerchantPlan
  extends MerchantPlanSummary,
    Readonly<{
      id: string;
      planCode: MerchantPlanCode;
      appBundle: AppBundle;
      type: MerchantPlanType;
      description: string;
      defaultPlan?: boolean;
      modules?: Elements<MerchantPlanModule>;
      merchantPlanGroup?: MerchantPlanGroup;
    }> {}

export interface MerchantPlanSummary
  extends Ref,
    Readonly<{
      name: MerchantPlanName;
      trialExpirationTime?: number;
    }> {}

export interface MerchantSummary extends BaseModel {
  eligibleForBoarding: boolean;
  type: BusinessType;
  isSingleLocationBusiness: boolean;
  role: RoleType;
  entityRef?: Partial<Merchant>;
}

export type SelfBoardingApplicationStatus =
  | 'REVIEW_ORDER'
  | 'ORDER_PENDING'
  | 'SINGLE_OUTLET_FAILED'
  | 'CREDIT_APPROVED'
  | 'CREDIT_PENDING'
  | 'CREDIT_CHECK_TIMEOUT'
  | 'CREDIT_DECLINED'
  | 'MANUAL_CREDIT_REVIEW'
  | 'APPLICATION_DECLINED'
  | 'APPLICATION_SUBMITTED'
  | 'APPLICATION_SIGNED'
  | 'APPROVAL_IN_PROGRESS'
  | 'APPLICATION_ABANDONED'
  | 'PENDING_DATA_VALIDATION'
  | 'HARDWARE_IS_BEING_SHIPPED'
  | 'ACCOUNT_APPROVED'
  | 'ACCOUNT_SET_UP_IN_PROGRESS'
  | 'ACCOUNT_PENDING'
  | 'ACCOUNT_READY_FOR_PROCESSING'
  | 'BOARDED_VIA_SALES'
  | 'CONDITIONALLY_APPROVED'
  | 'ACCOUNT_CONDITIONALLY_READY'
  | 'CONDITIONALLY_APPROVED_HIGH_RISK'
  | 'CONDITIONALLY_APPROVED_LOW_RISK';

export interface MerchantGateway {
  accountName: string;
  production: boolean;
}
export interface Merchant extends BaseModel {
  readonly phoneNumber?: string;
  readonly address: MerchantAddress;
  readonly associatedEnterprise?: Uuid;
  readonly associatedEnterpriseName?: string;
  readonly billingInfo: MerchantBillingInfo;
  readonly createdTime: number;
  readonly compliances?: Compliances;
  readonly customerContactEmail?: string;
  readonly devices?: Elements<Device>;
  readonly isBillable: boolean;
  readonly logos?: MerchantLogo[];
  readonly merchantGroups?: MerchantGroup[];
  readonly merchantPlan: MerchantPlan;
  readonly merchantPrivacyPolicyUrl?: string;
  readonly owner: Partial<Employee>;
  readonly partnerApp?: App;
  readonly properties: MerchantProperties;
  readonly reseller?: Reseller;
  readonly selfBoardingApplication?: {
    applicationStatus: SelfBoardingApplicationStatus;
  };
  readonly taskList?: Task[];
  readonly website?: string;
  readonly gateway?: MerchantGateway;
}

export type MerchantBillingInfo = {
   [P in 'appBillable' | 'planBillable' | 'wmBillable']: boolean;
};

export type AppVersion = Ref & {
  app: App;
  associationTime: number;
  deviceType?: DeviceType;
  merchantGroup: Ref;
  version: AndroidVersion;
};

export interface Rom extends Ref {
  createdAt: number;
  version: number;
  versionName: string;
  description: string;
  buildType: string;
  isCritical: boolean;
  published: boolean;
  enabled: boolean;
  associationTime: number;
}

export interface SystemAppConfig extends BaseModel {
  configurable: boolean;
  hidden: boolean;
  isvDeveloper: { id: Uuid };
  modifiedTime: number;
  systemApp: App;
}

export interface EmailType extends Ref {
  type: string;
  typeGroup: string;
}

export interface EmailOverrride extends Ref {
  developerId: Uuid;
  emailTypeId: Uuid;
  overrideAddress: string;
  sendEmail: boolean;
  emailType: string;
  emailTypeGroup: string;
  createdTime?: number;
  modifiedTime?: number;
}

export interface MerchantGroup
  extends BaseModel,
    Readonly<{
      appVersions?: Elements<AppVersion>;
      androidVersions?: Elements<AndroidVersion>;
      createdTime: number;
      merchantCount?: number;
      merchants?: Elements<Merchant>;
      merchantsCreatedAfterTime?: number; // CLOVERDEV only
      merchantsCreatedBeforeTime?: number; // CLOVERDEV only
      roms?: Elements<Rom>;
      skippedUuids?: Elements<Uuid>;
    }> {}

export type MerchantProperties = Readonly<{
  abaAccountNumber?: string;
  ddaAccountNumber?: string;
  bankMarker?: number;
  defaultCurrency: CurrencyCode;
  deleteOrders: boolean;
  hasDefaultEmployee?: boolean;
  onPaperTipSignatures?: boolean;
  resetOnReportingTime?: boolean;
  notesOnOrders?: boolean;
  removeTaxEnabled?: boolean;
  groupLineItems?: boolean;
  showCloseoutOrders?: boolean;
  sendCloseoutEmail?: boolean;
  stayInCategory?: boolean;
  alternateInventoryNames?: boolean;
  updateStock?: boolean;
  autoPrint?: boolean;
  infoleaseSuppressBilling?: boolean;
  infoleaseSuppressPlanBilling?: boolean;
  allowClockOutWithOpenOrders?: boolean;
  logInClockInPrompt?: boolean;
  hasConsented?: boolean;
  printedFirstDataReceiptLogoEnabled?: boolean;
  disablePrintTaxesPaymentOnReceipts?: boolean;
  limpModeAllowed2?: boolean;
  marketingEnabled?: boolean;
  autoLogout?: boolean;
  locale: FullLanguageTag;
  processingRestrictions: ProcessingRestrictions;
  summaryHour: number;
  timezone: string;
  supportPhone?: string; // supportPhone and supportEmail are null when the reseller is 'Demo'
  supportEmail?: string;
  pinLength?: number;
  signatureThreshold?: number;
  maxCashBack?: number;
  tipRateDefault?: number;
  orderTitleMax?: number;
  orderTitle?: string;
  vatTaxName?: string;
  cashBackOptions?: string;
  appBillingSystem?: string;
  autoCloseoutTimezone?: string;
  shippingAddress?: string;
  privacyPolicyMode?: string;
  merchantPrivacyPolicyUrl?: string;
  merchantBoardingStatus?: SelfBoardingApplicationStatus;
  tipsEnabled?: boolean;
  manualCloseout?: boolean;
  manualCloseoutPerDevice?: boolean;
  cashBackEnabled?: boolean;
  vat?: boolean;
  trackStock: boolean;
}>;

export type ProcessingRestrictions = 'FAILED' | 'PENDING' | 'RESTRICTED' | 'NONE';

export type AuthorizationFrontEnd = 'Nashville' | 'Omaha' | 'Buypass';

export interface DeveloperSummary extends BaseModel, Optional<{ readonly website: string }> {}

export type DeveloperStatus = 'NEW' | 'PENDING' | 'APPROVED' | 'DENIED';

export type AppDistribution = 'PUBLIC' | 'PRIVATE';

export interface DeveloperRole extends BaseModel {
  accounts: Elements<AccountSummary>;
  label?: string;
  permissions: Elements<DeveloperPermission>;
  system: boolean;
}

export enum DeveloperMemberStatus {
  ACTIVE = 'active',
  DEACTIVATED = 'deactivated',
  INVITED = 'invited',
}

export interface DeveloperMember extends BaseModel {
  createdTime: number;
  email: string;
  inviteSent: boolean;
  isActive: boolean;
  lastLogin: number;
  role: DeveloperRole;
  status: DeveloperMemberStatus;
}

export type DeveloperBillingInfo = Ref & {
  bankAccountNumber: string;
  bankRoutingNumber: string;
  vatRegisterNumber?: string;
};

export type BankInfo = {
  bankAccountLastFour: string;
  modifiedTime: number;
};

export interface DeveloperIdentity {
  dob: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
}

export interface PRContact {
  prEmail: string;
  prName: string;
  prPhone: string;
}

export interface AccountSummary
  extends Pick<BaseModel, 'id'>,
    Readonly<{
      name?: string;
      email: string;
    }> {}

export interface Account
  extends AccountSummary,
    Readonly<{
      claimedTime?: number;
      isActive: boolean;
      authFactors?: TwoFaEntity[];
      authLevel?: number;
      allowEnterpriseBoarding: boolean;
      primaryMerchant: Merchant;
      primaryDeveloper?: DeveloperSummary;
      csrfToken?: string;
    }> {}

export interface AccountWithIds
  extends Account,
    Readonly<{
      primaryDeveloperId?: string;
      primaryEnterpriseId?: string;
      primaryMerchantId: string;
      primaryResellerId?: string;
    }> {}

export const AppSegment = objectEnum('APP_MARKET', 'SEMI_INT', 'MERCHANT_COMPANION', 'CLOVER_GO');

export type AppSegmentType = Value<typeof AppSegment>;

export type AppApprovalStatus = 'NEW' | 'PENDING' | 'APPROVED' | 'DENIED' | 'PUBLISHED';

export type AndroidVersionApprovalStatus = AppApprovalStatus | 'APPROVED_PENDING_SIGNING';

export type BillingApprovalStatus = 'APPROVED' | 'DECLINED' | 'ERROR' | 'PENDING';

export const VTMode = objectEnum('TAKE_PAYMENT', 'SEND_INVOICE');

export type VTModeType = Value<typeof VTMode>;

export const InvoiceStatus = objectEnum(
  'CREATED',
  'SENT',
  'VIEWED',
  'REMINDED',
  'NOTIFIED',
  'UNRECOGNIZED',
  'NOTIFICATION_FAILURE',
);

export type InvoiceStatusType = Value<typeof InvoiceStatus>;

export const InvoicePaymentStatus = objectEnum('PAID', 'UNPAID', 'IN_PROCESS');

export type InvoicePaymentStatusType = Value<typeof InvoicePaymentStatus>;

export type Webhook = {
  app: boolean;
  cashAdjustment: boolean;
  customers: boolean;
  employees: boolean;
  exports: boolean;
  inventory: boolean;
  merchants: boolean;
  orders: boolean;
  payments: boolean;
  secret: string;
  url: string;
  valid: boolean;
};

export type AppPermissionName =
  | 'CUSTOMERS_R'
  | 'CUSTOMERS_ADDRESS_R'
  | 'CUSTOMERS_BIRTHDATE_R'
  | 'CUSTOMERS_BUSINESSNAME_R'
  | 'CUSTOMERS_CARD_R'
  | 'CUSTOMERS_EMAIL_R'
  | 'CUSTOMERS_NOTE_R'
  | 'CUSTOMERS_PHONE_R'
  | 'CUSTOMERS_MARKETING_R'
  | 'CUSTOMERS_W'
  | 'CUSTOMERS_ADDRESS_W'
  | 'CUSTOMERS_BIRTHDATE_W'
  | 'CUSTOMERS_BUSINESSNAME_W'
  | 'CUSTOMERS_CARD_W'
  | 'CUSTOMERS_EMAIL_W'
  | 'CUSTOMERS_NOTE_W'
  | 'CUSTOMERS_PHONE_W'
  | 'CUSTOMERS_MARKETING_W'
  | 'EMPLOYEES_R'
  | 'EMPLOYEES_W'
  | 'INVENTORY_R'
  | 'INVENTORY_W'
  | 'MERCHANT_R'
  | 'MERCHANT_W'
  | 'MID_R'
  | 'ORDERS_R'
  | 'ORDERS_W'
  | 'PAYMENTS_R'
  | 'PAYMENTS_W'
  | 'PROCESS_CARDS';

export type AppPermission = {
  appPermission: {
    name: AppPermissionName;
  };
  justification?: string;
  required: boolean;
};

export interface AppLocale {
  filenameIconSmall: string;
  locale: string;
  name: string;
}

export interface SuggestedApp {
  app: BaseModel & {
    filenameIconSmall: string;
    locales: Elements<AppLocale>;
  };
  score: number;
  highlight?: {
    description?: string;
    tagline?: string;
  }
}

export interface AppSearchInfo {
  currentPage: number;
  numPages: number;
  perPage: number;
  query: string;
  totalResultCount: number;
}

export interface AppSearchAutoComplete {
  info?: Partial<AppSearchInfo>;
  records: Elements<SuggestedApp>
}

export interface App extends BaseModel {
  activationUrl?: string;
  aggregateRating: AggregateRating;
  androidVersion?: AndroidVersion;
  androidVersions?: Elements<AndroidVersion>;
  appDomain?: string;
  applicationId?: Uuid;
  approvalStatus: AppApprovalStatus;
  approvalStatusModifiedTime: number;
  appSecret?: string;
  availableSubscriptions?: Elements<AppSubscription>;
  availableMetereds?: Elements<AppMeteredEvent>;
  benefits?: Elements<string>;
  billingStartTime: number;
  billingInfo?:BillingInformation,
  categories?: Elements<AppCategory>;
  createdTime: number;
  currentSubscription?: AppSubscription
  defaultResponseType: string;
  description?: string;
  developer: DeveloperSummary;
  deviceTypes?: Elements<DeviceType>;
  distribution: AppDistribution;
  ecommIntegrationType?: string;
  editorPick?: boolean;
  equipmentCode?: string;
  equipmentName?: string;
  eula?: string;
  filenameCover?: string;
  filenameBanner?: string;
  filenameIcon?: string;
  filenameIconLarge?: string;
  filenameIconSmall?: string;
  firstApprovalTime: number;
  firstPublishedTime: number;
  firstSubmittedTime?: number;
  hidden: boolean;
  installed?: boolean;
  installCount: number;
  isHipaaCompliant?: boolean;
  linkLabel?: string;
  locale: string;
  locales?: Elements<LocalizedApp>;
  metereds?: Elements<AppMeteredEvent>;
  modules?: Elements<MerchantPlanModule>;
  nonCloverBilling?: boolean;
  packageName?: string;
  paidAppHasTrial?: boolean;
  permissionCustomersAddressRead?: boolean;
  permissionCustomersAddressWrite?: boolean;
  permissionCustomersBirthdateRead?: boolean;
  permissionCustomersBirthdateWrite?: boolean;
  permissionCustomersBusinessnameRead?: boolean;
  permissionCustomersBusinessnameWrite?: boolean;
  permissionCustomersCardRead?: boolean;
  permissionCustomersCardWrite?: boolean;
  permissionCustomersEmailRead?: boolean;
  permissionCustomersEmailWrite?: boolean;
  permissionCustomersMarketingRead?: boolean;
  permissionCustomersMarketingWrite?: boolean;
  permissionCustomersNoteRead?: boolean;
  permissionCustomersNoteWrite?: boolean;
  permissionCustomersPhoneRead?: boolean;
  permissionCustomersPhoneWrite?: boolean;
  permissionCustomersRead?: boolean;
  permissionCustomersWrite?: boolean;
  permissionEmployeesRead?: boolean;
  permissionEmployeesWrite?: boolean;
  permissionInventoryRead?: boolean;
  permissionInventoryWrite?: boolean;
  permissionMerchantRead?: boolean;
  permissionMerchantWrite?: boolean;
  permissionMidRead?: boolean;
  permissionOrdersRead?: boolean;
  permissionOrdersWrite?: boolean;
  permissionPaymentsRead?: boolean;
  permissionPaymentsWrite?: boolean;
  permissionProcessCards?: boolean;
  permissions?: Elements<AppPermission>;
  popularity?: number;
  privacyPolicy?: string;
  published?: boolean;
  redirectUrl?: string;
  screenshots?: Elements<Screenshot>;
  segment?: AppSegmentType;
  siteUrl?: string;
  launchUrlPath?: string;
  sortOrder?: number;
  subscriptions?: Elements<AppSubscription>;
  supportEmail?: string;
  supportPhone?: string;
  supportUrl?: string;
  systemApp?: boolean;
  tagline?: string;
  trialDays?: number;
  trialDaysLeft?: number;
  videoUrl?: string;
  webhook?: Webhook;
}

export interface MerchantApp extends App {
  installed: boolean;
  installedTime: number;
}

export interface CategoryApp extends Ref {
  app: App,
  category: AppCategory
}

export interface AppBundle extends Ref {
  name?: string;
  price?: number;
  pricePerDevice?: number;
  includedApps?: Elements<MerchantApp>
  bundleCountries?: Elements<AppBundleCountry>;
}

export type AppBundleCountry = {
  id: string;
  price: number;
  pricePerDevice: number;
  country: CountryCode;
};

export type AppReviewAuthor = {
  isSelf: boolean;
  name: string;
};

export type DeveloperResponse = {
  body: string;
  createdTime: number;
  modifiedTime: number;
  developerReference: {
    id: Uuid;
    name: string;
  };
  id: Uuid;
};

export type DeveloperAppReview = {
  appSubscription: Pick<AppSubscription, 'id'>;
  auditStatus: 'APPROVED' | 'NEW' | 'REJECTED';
  author: AppReviewAuthor;
  body: string;
  createdTime: number;
  developerApp: {
    id: Uuid;
    name: string;
  };
  id: Uuid;
  modifiedTime:number;
  response?: DeveloperResponse;
  starRating: number;
  title: string;
};

export type AggregateRating = {
  reviewCount: number;
  totalStars: number;
  weightedRating : number;
};

export interface Screenshot {
  large: string;
  locale: string;
  medium: string;
  name: string;
  small: string;
  sortOrder: number;
}

export type ReleaseNote = Ref & {
  note?: string;
};

export type AndroidVersion = Ref & {
  apkUrl: string;
  app: App;
  approvalStatus: AndroidVersionApprovalStatus;
  createdAt: number;
  deviceInstallCount?: number;
  submittedAt: number;
  version: number;
  versionName: string;
  releaseNote?: ReleaseNote;
};

export interface LocalizedApp extends Mutable<BaseModel> {
  app: Ref;
  benefits?: Elements<string>;
  categories?: Elements<AppCategory>;
  description?: string;
  eula?: string;
  filenameBanner?: string;
  filenameCover?: string;
  filenameIcon?: string;
  filenameIconLarge?: string;
  filenameIconSmall?: string;
  locale: string;
  privacyPolicy?: string;
  screenshots?: Elements<Screenshot>;
  smartReceiptText?: string;
  smartReceiptUrl?: string;
  supportEmail?: string;
  supportPhone?: string;
  supportPhoneHours?: string;
  supportUrl?: string;
  tagline?: string;
  videoUrl?: string;
}

export interface AppSubscription extends Ref {
  label: string;
  plan?: boolean;
  subscriptionCountries: Elements<AppSubscriptionCountry>;
}

export interface AppSubscriptionCountry extends Mutable<BaseModel> {
  active: boolean;
  amount: number;
  appSubscription: Mutable<Ref>;
  country: CountryCode;
  description: string;
  installCount?: number;
}

export interface AppSubscriptionEvent {
  appSubscription?: AppSubscription;
  createdTime?: number;
  deletedTime?: number;
}

export interface BaseAppMeteredEvent extends Ref {
  action: string;
  active: boolean;
  amount: number;
}

export interface AppMeteredEvent extends BaseAppMeteredEvent {
  app: Ref;
  meteredCountries: Elements<AppMeteredEventCountry>;
  label: string;
}

export interface AppMeteredEventCountry extends BaseAppMeteredEvent {
  appMetered: Mutable<Ref>;
  country: CountryCode;
}

type AppCollectionPopulatedByType = 'HAND_CURATED' | 'SCHEDULED_JOB';

type AppCollectionStyleType =
  | AppCollectionStyles.VerticalGrid
  | AppCollectionStyles.ScrollGridTile
  | AppCollectionStyles.ScrollTile
  | AppCollectionStyles.ScrollHeroBanner

export interface AppCollection extends Mutable<BaseModel> {
  appPopulatedBy?: AppCollectionPopulatedByType;
  autoInstall?: boolean;
  carouselApps: Elements<App>;
  collectionStyle?: AppCollectionStyleType;
  collectionGroup?: AppCollectionGroup;
  countryCode?: string;
  displayName?: string;
  maxSize?: number;
  showInAppDetail: boolean;
  showInAppMarket: boolean;
  showInDashboard: boolean;
}

export interface GlobalAppCollection extends Mutable<BaseModel> {
  appPopulatedBy?: AppCollectionPopulatedByType;
  autoInstall?: boolean;
  carouselApps: App[];
  collectionGroup?: AppCollectionGroup;
  collectionStyle?: AppCollectionStyleType;
  countryCode?: string;
  displayName?: string;
  maxSize?: number;
  showInAppDetail: boolean;
  showInAppMarket: boolean;
  showInDashboard: boolean;
}

export interface ServerFeatureConfig {
  isGloballyWhitelisted?: boolean;
  blacklistedApps?: Uuid[];
  blacklistedDevelopers?: Uuid[];
  visibilityThreshold?: number
}

type BlackListedMap = Record<Uuid, boolean>;

export interface NormalizedServerFeature extends BaseModel {
  enabled: boolean;
  isGloballyWhitelisted?: boolean;
  blacklistedApps: BlackListedMap;
  blacklistedDevelopers: BlackListedMap;
  visibilityThreshold?: number;
}

export interface ServerFeature extends BaseModel {
  config?: ServerFeatureConfig | string,
  enabled: boolean
}

export interface DeviceType extends Ref {
  displayName: string;
  enabled?: boolean;
  models: string;
  name: DeviceCodeName;
  sdkVersion?: number;
}

export interface MerchantApp extends App {
  installed: boolean;
  installedTime: number;
}

export type AppCategoryType = 'FUNCTIONAL' | 'DOMAIN';

export interface AppCategory extends BaseModel {
  children?: AppCategory[];
  group?: BaseModel;
  type: AppCategoryType;
}

export interface Permission extends BaseModel,
    Readonly<{
      href: string;
      app: App; // App is expanded in the api request
      employeeDefault: boolean;
      label: string;
      managerDefault: boolean;
    }> {}

export interface AppMarketPermission extends Permission {
  readonly name: AppMarketPermissions
}
export type DeviceProvision = DeepReadonly<{
  currentRom?: Rom;
  currentSecureBoardRom?: Rom;
  deviceType: DeviceTypeName;
  provisionedTime?: number;
  lastActivationCode: string;
  activationCode: string;
  emailSent: boolean;
  hasShipment: boolean;
  serialNumber: string;
  merchant: Ref;
}>;

export type DeviceState =
  | 'ASSOCIATED'
  | 'REGISTERED'
  | 'PROVISIONED'
  | 'ACTIVATED'
  | 'REACTIVATED'
  | 'DEACTIVATED'
  | 'DISASSOCIATED'
  | 'POS_MODE'
  | 'CFD_MODE';

export interface DevKitDevice extends DeviceProvision {
  activatedTime: number;
  deviceRef: Ref & { href: string };
  deviceType: DeviceTypeName;
  merchant: Merchant;
  provisionedTime: number;
  reseller: Ref;
  state: DeviceState;
}

export type PaymentResult =
  | 'SUCCESS'
  | 'FAIL'
  | 'INITIATED'
  | 'VOIDED'
  | 'VOIDING'
  | 'VOID_FAILED'
  | 'AUTH'
  | 'AUTH_COMPLETED';

export type CardType =
  | 'AMEX'
  | 'CARTE_BLANCHE'
  | 'CHINA_UNION_PAY'
  | 'DINERS_CLUB'
  | 'DISCOVER'
  | 'EBT'
  | 'GIFT_CARD'
  | 'GIROCARD'
  | 'INTERAC'
  | 'JCB'
  | 'LASER'
  | 'MAESTRO'
  | 'MC'
  | 'OTHER'
  | 'SOLO'
  | 'UNKNOWN'
  | 'VISA';

type Tender = Ref & {
  editable: boolean;
  enabled: boolean;
  label: string;
  labelKey: string;
  opensCashDrawer: boolean;
  visible: boolean;
};

type CardTransaction = {
  cardType: CardType;
};

export type Payment = Ref & {
  cardTransaction?: CardTransaction;
  order: Ref;
  tender?: Tender;
  overrideMerchantTender?: Tender;
  amount: number;
  taxAmount: number;
  createdTime: number;
  clientCreatedTime: number;
  modifiedTime: number;
  result: PaymentResult;
};

export interface Role extends BaseModel {
  systemRole: RoleType;
}

export interface Employee extends BaseModel {
  nickname?: string;
  email: string;
  inviteSent: boolean;
  claimedTime: number;
  pin?: string;
  role: RoleType;
  roles?: Role[];
  isOwner: boolean;
  customId?: string;
}

export interface School {
  schoolId: number;
  schoolName: string;
  cityName: string;
  stateCode: string;
}

export type FormField = {
  name: string;
  value: string;
  errorMsg?: string;
  hasError: boolean;
  required: boolean;
  touched: boolean;
  isValueValid?: <T>(value: T) => boolean;
};

export type EmailAddress = {
  emailAddress: string;
};

export type PhoneNumber = {
  phoneNumber: string;
};

export type Address = {
  address1: string;
  address2?: string;
  address3?: string;
  city?: string;
  country?: string;
  state?: string;
  zip?: string;
};

export type EcommAddress = {
  line1: string;
  line2?: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
};

export type Shipping = Ref & {
  name: string;
  carrier: string;
  phone: string;
  tracking_number: string;
  address: EcommAddress;
};

export type CustomerMetadata = {
  businessName: string;
};

// schema-tool/schemas/src/main/avro/customers/customer.avsc
export interface Customer {
  firstName?: string;
  lastName?: string;
  metadata?: CustomerMetadata;
  phoneNumbers?: PhoneNumber[];
  emailAddresses?: EmailAddress[];
  addresses?: Address[];
}

// schema-tool/schemas/src/main/avro/ecomm/customer.avsc
export interface EcommCustomer extends Ref {
  firstName?: string;
  lastName?: string;
  object?: string;
  email: string; // Primary unique email id to associate the card
  phone?: string; // Primary phone number against the cof
}

export interface Task extends BaseModel {
  type: TaskType;
  status: TaskStatus;
  descriptionTransKey: string;
}

export type PublicOrderDisplay = {
  publicOrder: Order;
  subtotal: number;
  totalTaxAmount: number;
  merchantName: string;
  merchantLogos: Elements<MerchantLogo>;
  supportedCardTypes: Elements<string>;
  tipsEnabled: boolean;
  tipSuggestions: Elements<TipSuggestion>;
  defaultCurrency: string;
  timestamp: number;
  token: string;
  discounts?: Elements<Discount>;
  feedbackToken?: string;
  serviceCharge?: ServiceCharge;
};

type OrderType = Ref & {
  labelKey: string;
  label: string;
  taxable: boolean;
  isDefault: boolean;
  filterCategories: boolean;
  isHidden: boolean;
  fee: number;
  minOrderAmount: number;
  maxOrderAmount: number;
  maxRadius: number;
  avgOrderTime: number;
  hoursAvailable: string;
  isDeleted: false;
  systemOrderTypeId: string;
};

export interface Order extends Ref {
  currency: string;
  title: string;
  total: number;
  state: string;
  employee: Employee;
  serviceCharge: ServiceCharge;
  discounts: Elements<Discount>;
  lineItems: Elements<LineItem>;
  isVat: boolean;
  createdTime: number;
  modifiedTime: number;
  orderType: OrderType;
}

export interface ServiceCharge extends Ref {
  name: string;
  enabled: boolean;
  percentage: number;
  percentageDecimal: number;
  orderRef?: Ref;
}

export interface Discount extends Mutable<BaseModel> {
  orderRef?: Ref;
  lineItemRef?: LineItem;
  amount?: number;
  percentage?: number;
}

export interface Modifier extends Mutable<BaseModel & Ref> {
  amount: number;
  lineItemRef: Ref;
  modifier: Ref;
}

export interface LineItem extends Mutable<BaseModel & Ref> {
  orderRef: Ref;
  item?: Ref;
  price: number;
  printed: boolean;
  binName: string;
  createdTime: number;
  orderClientCreatedTime: number;
  exchanged: boolean;
  refunded: boolean;
  isRevenue: boolean;
  count?: number;
  unitQty?: number;
  unitName?: string;
  modifications?: Elements<Modifier>;
  discounts?: Elements<Discount>;
}

export type TipSuggestion = Mutable<BaseModel> & {
  percentage: number;
  isDefault: boolean;
  isEnabled: boolean;
};

export type Charge = Ref & {
  amount: number;
  createdTime: number;
  currency: CurrencyCode;
  developerPortion?: number;
  endDate: number;
  exportMonth?: number;
  modifiedTime: number;
  startDate: number;
  status: ChargeStatus;
  statusOwner: string;
  statusModifiedTime?: number;
  systemType: string;
  tax: number;
  taxClassificationCode: string;
  type: ChargeType;
};

export type Transaction = Mutable<BaseModel> & {
  app: App;
  appInstallTime: number;
  appSubscription: AppSubscription;
  charge: Charge;
  createdTime: number;
  merchant: Merchant;
  modifiedTime: number;
};

export enum TransactionAction {
  CANCEL = 'CANCEL',
  CANCEL_REFUND = 'CANCEL_REFUND',
  NONE = 'NONE',
  REFUND = 'REFUND',
  UNDO_CANCEL = 'UNDO_CANCEL',
}

// Needed for V1 or until multiple currency support is added to Billing Transaction summary
export type BillingSummaryV1 = {
  currency?: CurrencyCode;
  num: number;
  totalAmount: number;
  totalTax: number;
  totalDeveloperPortion: number;
  numOfMerchants: number;
};

export type BillingSummary = {
  currency: CurrencyCode;
  num: number;
  totalAmount: number;
  totalTax: number;
  totalDeveloperPortion: number;
  numOfMerchants: number;
};

export type Currency = {
  currencyCode: CurrencyCode;
  displayName: string;
};

export type SourceTypes = 'ecom' | 's2p' | 't2p';
export type VTPaymentTypes = 'vt_legacy' | 'vt_cof' | 'vt_checkout';
export type PaymentPlatformTypes = 'apple_pay' | 'google_pay';

export type PaymentAttributes = {
  source_app?: string;
  source_type?: SourceTypes;
  vt_payment_type?: VTPaymentTypes;
  payment_platform?: PaymentPlatformTypes;
};

export interface Identifiable {
  id: Uuid;
}

export const isIdentifiable = (data: unknown): data is Identifiable => {
  return (data as Identifiable).id !== undefined;
};

export type MetricsType = 'install' | 'revenue' | 'uninstall';

export type MetricsOptions = {
  endTime?: number;
  filters?: string[];
  groupBy?: string;
  limit?: number;
  orderBy?: string;
  page?: number;
  searchVal?: string;
  startTime?: number;
};

export type MetricsDataResult = {
  tags: {
    appId?: Uuid;
    day?: number;
    billable?: boolean;
    country?: CountryCode;
    currency?: CurrencyCode;
    deviceTypes?: string[];
    merchantCountry?: CountryCode;
    merchantPlan?: string;
    merchantType?: string;
    month?: number;
    subscriptionId?: Uuid;
    year?: number;
  };
  timestamp?: number;
  value: number;
};

const VariantTypeEnum = objectEnum('EMPLOYEE_PIN');

export type VariantType = Value<typeof VariantTypeEnum>;

// Variants are used in Multi-Location management to track
// where locations differ from the "canonical" (standard) value.
// e.g. an item may in most cases be priced at $5, but in certain
// locations the price may be $6.
export type Variant = {
  name: VariantType;
  merchant: Uuid;
  value: boolean | number | string | undefined;
};

// Used to store references to single-location objects controlled by multi-location objects.
export type LinkData<T> = {
  canonicalObject: Ref | T; // holds the "default" single-location representation of this object
  entries: Link<T>[];
};

// Tuple containing single-location merchant and that merchant's copy of the object
// e.g. An EnterpriseItem at a 5-merchant location consists of up to 5 linked single-location
// Items, one for each merchant which has that item in its inventory.
export type Link<T> = {
  child: Ref | Merchant;
  object: Ref | T;
};

export type EnterpriseObject<MerchantObject> = {
  id: Uuid;
  variations?: Variant[];
  links?: LinkData<MerchantObject>;
  merchants?: Uuid[];
} & MerchantObject;

export type RevenueDetails = {
  latestTotalRevenue: number;
  previousTotalRevenue: number;
};

export interface MerchantContextEntity extends Merchant {
  role?: RoleType;
  roles?: Role[];
  type?: ContextType;
}
export type ContextEntity = MerchantContextEntity;
export interface AppConfig {
  context?: ContextEntity;
  account?: Account;
}
export interface GlobalState {
  state?: AppConfig;
}

export type BillingInformation = {
	planBillable: boolean,
	appBillable: boolean,
	planExportable: boolean,
	appExportable: boolean,
	termsAccepted: boolean
}

export type MerchantBillingInformation = {
  accountStatus: string,
  seasonal: boolean,
  appMarketConfig: {
     allowPaidAppInstalls: boolean,
     allowPaidAppUpgrades: boolean
  },
  billingInfo: BillingInformation,
  hardware: boolean
}
